import React from "react";
import styles from "./PrivacyPolicy.module.css";


const PrivacyPolicy = () => {
  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Privacy Policy</h2>
      <p className={styles.lastUpdated}>
        <strong>Last Updated: 5 Dec 2024</strong>
      </p>
      <p className={styles.intro}>
        Welcome to Grow Tube. This Privacy Policy explains how we collect, use,
        and protect your personal data when you use our mobile application.
      </p>

      <div className={styles.section}>
        <h3>1. Information We Collect</h3>
        <h4>1.1 Personal Data</h4>
        <p>
          We do not collect personal data beyond what is necessary to provide
          and improve our services. We may collect the following information:
        </p>
        <ul>
          <li>
            <strong>Usage Data:</strong> Information about how you interact with
            our app, such as the time spent on the app, the videos you watch,
            and the features you use.
          </li>
          <li>
            <strong>Interaction Data:</strong> Data on your interactions with
            videos, including play and pause events, as well as preferences and
            interests based on your viewing history.
          </li>
        </ul>
      </div>

      <div className={styles.section}>
        <h4>1.2 Tracking Technologies</h4>
        <p>
          We use the following tracking technologies to enhance your experience:
        </p>
        <ul>
          <li>
            <strong>Session Tracking:</strong> We monitor the time spent on the
            app to improve our services and user experience.
          </li>
          <li>
            <strong>Video Watch Time:</strong> We track how long you watch
            videos to help improve content recommendations and user engagement.
          </li>
          <li>
            <strong>Interest Tracking:</strong> We record your interactions with
            content, such as likes, shares, and comments, to better understand
            your preferences and provide personalized recommendations.
          </li>
        </ul>
      </div>

      <div className={styles.section}>
        <h3>2. How We Use Your Information</h3>
        <p>We use the information we collect for the following purposes:</p>
        <ul>
          <li>
            <strong>To Improve Our Services:</strong> Analyzing app usage and
            video watch time helps us understand user behavior and improve the
            app's functionality.
          </li>
          <li>
            <strong>To Personalize Content:</strong> Your interaction data helps
            us recommend videos and features that match your interests.
          </li>
          <li>
            <strong>To Ensure a Better User Experience:</strong> Session and
            interaction tracking enable us to provide a more engaging and
            tailored experience.
          </li>
          <li>
            <strong>Community Engagement and Points System:</strong>
            GrowTube enables YouTubers to share their content with a community
            of creators. Points earned are based on voluntary user engagement
            with videos and do not influence YouTube’s monetization metrics
            artificially
          </li>
        </ul>
      </div>

      <div className={styles.section}>
        <h3>3. Data Security</h3>
        <p>
          We implement appropriate technical and organizational measures to
          protect your data from unauthorized access, disclosure, alteration, or
          destruction. We use encryption and secure storage practices to
          safeguard your information. User data is securely stored in Firebase,
          which is compliant with data protection regulations.
        </p>
      </div>

      <div className={styles.section}>
        <h3>4. Data Sharing</h3>
        <p>
          We do not sell or share your personal data with third parties except
          as required by law or with trusted partners who assist us in operating
          our app and providing services. These partners are bound by
          confidentiality agreements and are only permitted to use your data for
          the purposes outlined in this policy.
        </p>
      </div>
      <div className={styles.section}>
        <h3>5. Purpose Clarification and Compliance with Google Policies</h3>
        <p>
          GrowTube is committed to adhering to Google’s policies regarding app
          content and user engagement. The primary purpose of this app is to
          help content creators and YouTubers grow their channels by encouraging
          mutual support through video watch time. However, we ensure that:
        </p>
        <ul>
          <li>
            <strong>No Artificial Watch Time or Views:</strong> We do not
            display advertisements that are tailored to individual users based
            on their personal data or usage behavior within the app.
          </li>
          <li>
            <strong>No Violation of YouTube’s Monetization Policies:</strong>
            Our app does not allow users to manipulate or bypass YouTube's watch
            time requirements. The content watched in the app is selected by
            users and is intended to help creators meet their YouTube
            monetization goals through organic engagement, not artificial means.
          </li>
          <li>
            <strong>No Forced Watch Time: </strong>
            Users are free to engage with the content at their own pace and
            discretion. We do not mandate watching videos or impose any kind of
            forced viewing system to artificially inflate watch time.
          </li>
        </ul>
      </div>
      <div className={styles.section}>
        <h3>6. Compliance Statement Regarding User Data</h3>
        <p>
          At GrowTube, we prioritize the privacy and security of our users'
          data. We want to clarify our practices regarding user data to ensure
          transparency and compliance with Google’s policies. Specifically, we
          do not engage in any of the following activities:
        </p>
        <ul>
          <li>
            <strong>Targeted Advertising:</strong> We do not display
            advertisements that are tailored to individual users based on their
            personal data or usage behavior within the app.
          </li>
          <li>
            <strong>Selling to Data Brokers:</strong> We do not sell user data
            to third-party data brokers or any other organizations for
            commercial purposes.
          </li>
          <li>
            <strong>Providing to Information Resellers:</strong> We do not
            provide or share user data with third parties who resell information
            or aggregate data for any reason.
          </li>
          <li>
            <strong>Determining Credit-Worthiness:</strong> Our application does
            not involve financial services or functionalities that require the
            assessment of users' creditworthiness.
          </li>
          <li>
            <strong>Lending Purposes:</strong> We do not facilitate lending or
            borrowing services that would require access to users' personal or
            financial information.
          </li>
          <li>
            <strong>User Advertisements:</strong> We do not allow users to
            create, manage, or display advertisements based on their personal
            data or interactions within our app.
          </li>
          <li>
            <strong>Personalized Advertisements:</strong> Our app does not
            utilize personal data to display ads that are customized according
            to users' interests or online behavior.
          </li>
          <li>
            <strong>Retargeted Advertisements:</strong> We do not employ any
            technologies that track users across websites or applications to
            display retargeted ads based on their previous interactions.
          </li>
          <li>
            <strong>Interest-Based Advertisements:</strong> We do not use
            personal data to show ads that are based on users’ interests,
            preferences, or browsing history.
          </li>
        </ul>
        <p>
          We are committed to safeguarding your personal information and
          ensuring that our practices align with the highest standards of
          privacy and data protection.
        </p>
      </div>

      <div className={styles.section}>
        <h3>7. Your Rights</h3>
        <p>You have the following rights regarding your personal data:</p>
        <ul>
          <li>
            <strong>Access:</strong> You can request access to the information
            we hold about you.
          </li>
          <li>
            <strong>Correction:</strong> You can request correction of
            inaccurate or incomplete data.
          </li>
          <li>
            <strong>Deletion:</strong> You can request deletion of your data,
            subject to legal and contractual obligations.
          </li>
          <li>
            <strong>Opt-Out:</strong> You can opt out of tracking features by
            adjusting your app settings or contacting us directly.
          </li>
        </ul>
      </div>

      <div className={styles.section}>
        <h3>8. Changes to This Policy</h3>
        <p>
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page, and we will notify you of significant updates.
          Your continued use of the app after any changes constitutes your
          acceptance of the updated policy.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
